import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { compact } from 'lodash'
import { OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import { Calendar, Plus, Target, People, Pencil, Delete } from '@factorialco/f0-react/icons/app'
import { IncentivesCompensationPlansDataCollectionDocument } from 'generated/resources/graphql'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import i18n from 'modules/core/lib/i18n'
import { useAiChat } from '@factorialco/f0-react'
import { recurrenceLabel } from 'modules/incentives/lib/recurrence'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { CreateCompensationPlanDialog } from 'modules/incentives/components/CreateCompensationPlanDialog'
import { DeleteCompensationPlanDialog } from 'modules/incentives/components/DeleteCompensationPlanDialog'

interface Props {
  canCreate: boolean
}

export const CompensationPlansDataCollection = ({ canCreate }: Props) => {
  const { company } = useCurrentSession()
  const { navigation } = useNavigation()
  const navigate = useNavigate()
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false)
  const [planToDelete, setPlanToDelete] = useState<string | null>(null)
  const aiChat = useAiChat()

  const onClickNewPlan = () => {
    if (aiChat.enabled) {
      // Reset the ONE chat conversation so the new incentive-plan creation flow
      // starts from a clean state, regardless of any prior message history. (FCT-53282)
      aiChat.clear()
      // TODO: Investigate why when passing only one placeholder it's completely ignored
      aiChat.setPlaceholders([
        i18n.t('incentives.list.new.one_placeholder'),
        i18n.t('incentives.list.new.one_placeholder'),
      ])
      aiChat.setInitialMessage(i18n.t('incentives.list.new.one_prompt'))
      aiChat.setVisualizationMode('fullscreen')
      aiChat.setOpen(true)
    } else {
      setIsCreateDialogOpen(true)
    }
  }

  const source = useGraphqlDataCollectionSource({
    totalItemSummary: true,
    document: IncentivesCompensationPlansDataCollectionDocument,
    perPage: 20,
    mapData: (data) => data.incentives.compensationPlansConnection.nodes || [],
    mapPagination: (data) => ({
      total: data.incentives.compensationPlansConnection.totalCount ?? 0,
    }),
    mapVariables: ({ search }) => ({
      companyId: company.id,
      search,
    }),
    search: {
      enabled: true,
    },
    primaryActions: canCreate
      ? () => ({
          label: i18n.t('incentives.list.new.label'),
          icon: Plus,
          onClick: () => {
            onClickNewPlan()
          },
        })
      : undefined,
    itemActions: (item) =>
      compact([
        item._policies?.update.can && {
          label: i18n.t('incentives.detail.update_plan.label'),
          icon: Pencil,
          onClick: () => {
            if (aiChat.enabled) {
              aiChat.setOpen(true)
              aiChat.sendMessage(
                i18n.t('incentives.detail.update_plan.one_prompt', { planName: item.name })
              )
            }
          },
        },
        item._policies?.delete.can && {
          label: i18n.t('incentives.detail.delete_plan.label'),
          icon: Delete,
          critical: true,
          onClick: () => {
            setPlanToDelete(item.id)
          },
        },
      ]),
    itemUrl: (item) =>
      navigation.compensations.incentives.plans.id.fullPath({ id: String(item.id) }),
  })

  const emptyState = {
    'no-data': {
      emoji: '💰',
      title: i18n.t('incentives.list.empty_state.label'),
      description: i18n.t('incentives.list.empty_state.description'),
      actions: canCreate
        ? [
            {
              label: i18n.t('incentives.list.new.label'),
              variant: 'outline' as const,
              onClick: () => onClickNewPlan(),
            },
          ]
        : undefined,
    },
  }

  return (
    <>
      <OneDataCollection
        fullHeight
        source={source}
        emptyStates={emptyState}
        visualizations={[
          {
            type: 'card',
            options: {
              title: (item) => item.name,
              cardProperties: [
                {
                  label: i18n.t('incentives.plan.recurrence'),
                  icon: Calendar,
                  render: ({ recurrence }) => recurrenceLabel(recurrence),
                },
                {
                  label: i18n.t('incentives.list.employees.label'),
                  icon: People,
                  render: ({ assignmentEmployeesCount }) =>
                    i18n.tp('incentives.list.employees.content', {
                      count: assignmentEmployeesCount,
                    }),
                },
                {
                  label: i18n.t('incentives.list.goals.label'),
                  icon: Target,
                  render: ({ currentPeriod }) => {
                    const metricsCount = currentPeriod?.metricsCount || 0

                    if (metricsCount > 0) {
                      return i18n.tp('incentives.list.goals.content', {
                        count: metricsCount,
                      })
                    }
                    return {
                      type: 'alertTag',
                      value: {
                        level: 'warning',
                        label: i18n.t('incentives.list.goals.alert'),
                      },
                    }
                  },
                },
              ],
            },
          },
        ]}
      />
      <CreateCompensationPlanDialog
        isOpen={isCreateDialogOpen}
        onClose={() => setIsCreateDialogOpen(false)}
        onSuccess={(planId) =>
          navigate(navigation.compensations.incentives.plans.id.fullPath({ id: planId }))
        }
      />
      {planToDelete && (
        <DeleteCompensationPlanDialog
          isOpen={!!planToDelete}
          onClose={() => setPlanToDelete(null)}
          planId={planToDelete}
          onSuccess={() => setPlanToDelete(null)}
        />
      )}
    </>
  )
}
